import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    BooleanField,
    DateField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    BooleanInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    FunctionField,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import { CheckCircle, Cancel, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { VendorRecord } from '../../types/vendor.types';
import { VENDOR_API_OPERATION_COUNT, countEnabled } from './apiAccessOperations';

const VendorsFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('ra.action.search')}
                source="q"
                alwaysOn
            />
            <BooleanInput
                label={translate('resources.vendors.fields.isActive')}
                source="isActive"
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box onClick={() => navigate(`/vendors/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.name}
                            </Typography>
                            <Chip
                                icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                label={record.isActive ? 'Active' : 'Inactive'}
                                color={record.isActive ? 'success' : 'default'}
                                size="small"
                                sx={{ flexShrink: 0, '& .MuiChip-icon': { fontSize: '1rem' } }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {record.code}
                            </Typography>
                            <Chip label={record.vendorType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {`${countEnabled(record.apiAllowedOperations)} / ${VENDOR_API_OPERATION_COUNT} API`}
                            </Typography>
                            {record.contactEmail && (
                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.contactEmail}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const VendorsList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<VendorsFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField source="name" label={translate('resources.vendors.fields.name')} />
                        <TextField source="code" label={translate('resources.vendors.fields.code')} />
                        <FunctionField
                            label={translate('resources.vendors.fields.vendorType')}
                            render={(record: any) => (
                                <Chip
                                    label={translate(`resources.vendors.types.${record.vendorType}`) || record.vendorType}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        />
                        <EmailField source="contactEmail" label={translate('resources.vendors.fields.contactEmail')} />
                        <TextField source="contactPhone" label={translate('resources.vendors.fields.contactPhone')} />
                        <FunctionField
                            label={translate('resources.vendors.fields.isActive')}
                            render={(record: any) => (
                                <Chip
                                    icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                    label={record.isActive ? translate('ra.boolean.true') : translate('ra.boolean.false')}
                                    color={record.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{
                                        '& .MuiChip-icon': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                            )}
                        />
                        <FunctionField
                            label={translate('resources.vendors.fields.apisEnabled')}
                            render={(record: VendorRecord) => `${countEnabled(record.apiAllowedOperations)} / ${VENDOR_API_OPERATION_COUNT}`}
                        />
                        <DateField
                            source="createdAt"
                            label={translate('resources.vendors.fields.createdAt')}
                            showTime
                        />
                        <EditButton />
                        <ShowButton />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab color="primary" onClick={() => navigate('/vendors/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}>
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
